import clsx from 'clsx';
import { ComponentProps, ComponentType, forwardRef, JSX, PropsWithChildren } from 'react';
import { Placement } from '@floating-ui/react';
import { MenuToggle, ToggleMenuButtonProps } from '@stream-io/video-react-sdk';
import { isComponentType } from './utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

export type IconButtonWithMenuProps = PropsWithChildren<{
  active?: boolean;
  Menu?: ComponentType | JSX.Element;
  caption?: string;
  className?: string;
  menuPlacement?: Placement;
  menuOffset?: number;
  variant?: 'primary' | 'secondary';
  onMenuToggle?: (menuShown: boolean) => void;
}> &
  ComponentProps<'button'>;

export const CompositeButton = forwardRef<HTMLDivElement, IconButtonWithMenuProps>(
  function CompositeButton(
    {
      disabled,
      caption,
      children,
      className,
      active,
      Menu,
      menuPlacement,
      menuOffset,
      title,
      variant,
      onClick,
      onMenuToggle,
      ...restButtonProps
    },
    ref
  ) {
    return (
      <div
        className={clsx('str-video__composite-button', className, {
          'str-video__composite-button--caption': caption,
          'str-video__composite-button--menu': Menu,
        })}
        title={title}
        ref={ref}
      >
        <div
          className={clsx('str-video__composite-button__button-group', {
            'str-video__composite-button__button-group--active': active,
            'str-video__composite-button__button-group--active-primary':
              active && variant === 'primary',
            'str-video__composite-button__button-group--active-secondary':
              active && variant === 'secondary',
            'str-video__composite-button__button-group--disabled': disabled,
          })}
        >
          <button
            type="button"
            className="str-video__composite-button__button"
            onClick={(e) => {
              e.preventDefault();
              onClick?.(e);
            }}
            disabled={disabled}
            {...restButtonProps}
          >
            {children}
          </button>
          {Menu && (
            <MenuToggle
              offset={menuOffset}
              placement={menuPlacement}
              ToggleButton={DefaultToggleMenuButton}
              onToggle={onMenuToggle}
            >
              {isComponentType(Menu) ? <Menu /> : Menu}
            </MenuToggle>
          )}
        </div>
        {caption && <div className="str-video__composite-button__caption">{caption}</div>}
      </div>
    );
  }
);

const DefaultToggleMenuButton = forwardRef<HTMLButtonElement, ToggleMenuButtonProps>(
  function DefaultToggleMenuButton({ menuShown }, ref) {
    return (
      <button ref={ref} className="text-accent  -ml-8 px-2 z-99999">
        <div className="w-4 h-4">
          {menuShown ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </div>
      </button>
    );
  }
);
